/**
 * The workspace's GitHub App install, as the GitHub card on Workspace
 * Settings → Integrations shows it: its live details, its menu, and its
 * repository list.
 *
 * There used to be a card of its own here, `GitHubIntegrationBlock`, for
 * pull request checks, beside a separate GitHub Issues card. The two are one
 * card now (docs/mockups/github-one-card.html): the issue-tracker card
 * renders these parts for the tracker that signs in through the install.
 * The page owns the install record, so it calls `useGitHubInstall` once and
 * hands the results into that card.
 *
 * Menu items:
 *   1. Re-authorize / refresh access — redirects to the backend
 *      reauthorize endpoint, which sends the browser to GitHub's
 *      configure-repos page for THIS install (not the install picker).
 *   2. View on GitHub — the manage-installation URL, branched on account
 *      type. Org installs live under
 *      `github.com/organizations/<org>/settings/installations/<id>`; user
 *      installs under `github.com/settings/installations/<id>`. The flat
 *      form 404s for orgs.
 *   3. Copy installation ID.
 * Disconnect belongs to the card, which removes the install and the issue
 * connections signed in through it in one call.
 *
 * One lookup, one answer. This hook is the only place the install's live
 * details are fetched; the card reads its account, permissions and status
 * from here, so the repository list and "What OttoTester can do" can never
 * disagree about whether the App is there.
 *
 * Broken state: when GitHub confirms the App was uninstalled (before our
 * webhook removed the record), the repository list is replaced by "GitHub
 * App is no longer installed" with Reconnect and Remove. When GitHub just
 * can't be asked, the list says so and offers Try again: that says nothing
 * about whether the App is installed.
 */
import { useEffect, useState, useCallback } from 'react'
import { RefreshCw, ExternalLink, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { OwnerAvatar, type ProviderBlockMenuItem } from '@/components/ProviderBlock'
import {
  API_BASE,
  githubInstallationAPI,
  InstallationGoneError,
  type GitHubInstallationAccount,
  type GitHubInstallationDetails,
} from '@/lib/api'

/** Where the browser goes to install the GitHub App on this workspace. It
 * bounces to GitHub and back to Workspace Settings. */
export function githubAppInstallUrl(workspaceId: string): string {
  return `${API_BASE}/integrations/github/install?workspaceId=${encodeURIComponent(workspaceId)}`
}

/**
 * Build the GitHub "manage installation" URL, branching on account type.
 * Org installs MUST go through the `/organizations/<login>/` sub-path; the
 * flat `/settings/installations/<id>` form 404s for orgs.
 *
 * `account` is optional so the helper degrades to the user-shape URL when
 * the details call failed and the account is unknown.
 */
export function buildManageOnGitHubUrl(
  installationId: number,
  account: GitHubInstallationAccount | null,
): string {
  if (account?.type === 'Organization') {
    return `https://github.com/organizations/${encodeURIComponent(account.login)}/settings/installations/${installationId}`
  }
  return `https://github.com/settings/installations/${installationId}`
}

/** What GitHub last said about the install. `none`: there is no install
 * record. `gone`: GitHub confirmed it was uninstalled. `unknown`: GitHub
 * couldn't be asked. */
export type InstallStatus = 'none' | 'loading' | 'present' | 'gone' | 'unknown'

/** What the page knows about the install, for the card. */
export interface GitHubInstall {
  installationId: number | null
  /** The last good answer. Kept through a failed refresh, for the account
   * name; `status` says whether it is current. */
  details: GitHubInstallationDetails | null
  status: InstallStatus
  loading: boolean
  /** Ask GitHub again without blanking what is shown. */
  refresh: () => void
  /** Re-authorize, View on GitHub, Copy installation ID. */
  menuItems: ProviderBlockMenuItem[]
  /** Send the browser to GitHub to change which repositories it can see,
   * or to reconnect a broken install. */
  reauthorize: () => void
}

/** Load one install's live details and build its menu. `installationId`
 * null means there is no install; nothing is fetched. */
export function useGitHubInstall(workspaceId: string, installationId: number | null): GitHubInstall {
  const [details, setDetails] = useState<GitHubInstallationDetails | null>(null)
  const [status, setStatus] = useState<InstallStatus>(installationId ? 'loading' : 'none')
  const [check, setCheck] = useState(0)
  const [idCopied, setIdCopied] = useState(false)
  const [reauthorizing, setReauthorizing] = useState(false)

  // A new install starts from nothing; a refresh of the same one keeps what
  // is shown until GitHub answers.
  useEffect(() => {
    setDetails(null)
    setStatus(installationId ? 'loading' : 'none')
  }, [installationId])

  useEffect(() => {
    if (!installationId) return
    let cancelled = false
    githubInstallationAPI
      .getDetails(workspaceId, installationId)
      .then((d) => {
        if (cancelled) return
        setDetails(d)
        setStatus('present')
      })
      .catch((err) => {
        if (cancelled) return
        setStatus(err instanceof InstallationGoneError ? 'gone' : 'unknown')
      })
    return () => { cancelled = true }
  }, [workspaceId, installationId, check])

  const refresh = useCallback(() => setCheck((n) => n + 1), [])

  const reauthorize = useCallback(() => {
    if (!installationId || reauthorizing) return
    // The backend sends the browser to GitHub's configure-repos page with
    // this install pinned, so GitHub skips the account picker. The item is
    // disabled during the hand-off.
    setReauthorizing(true)
    window.location.assign(githubInstallationAPI.reauthorizeUrl(workspaceId, installationId))
  }, [workspaceId, installationId, reauthorizing])

  async function copyId() {
    if (!installationId) return
    try {
      await navigator.clipboard.writeText(String(installationId))
      setIdCopied(true)
      setTimeout(() => setIdCopied(false), 2000)
    } catch {
      // Clipboard unavailable: the id is visible in the card's subtitle.
    }
  }

  const menuItems: ProviderBlockMenuItem[] = [
    {
      key: 'reauthorize',
      label: reauthorizing ? 'Redirecting to GitHub…' : 'Re-authorize / refresh access',
      icon: <RefreshCw className={`h-3.5 w-3.5 ${reauthorizing ? 'animate-spin' : ''}`} />,
      disabled: !installationId || reauthorizing,
      onClick: reauthorize,
    },
    {
      key: 'view-on-github',
      label: 'View on GitHub',
      icon: <ExternalLink className="h-3.5 w-3.5" />,
      disabled: !installationId,
      onClick: () => {
        if (!installationId) return
        window.open(buildManageOnGitHubUrl(installationId, details?.account ?? null), '_blank', 'noopener,noreferrer')
      },
    },
    {
      key: 'copy-id',
      label: idCopied ? 'Copied!' : 'Copy installation ID',
      icon: idCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />,
      disabled: !installationId,
      onClick: () => { void copyId() },
    },
  ]

  return { installationId, details, status, loading: status === 'loading', refresh, menuItems, reauthorize }
}

/** "Installed on <account> · Organization · Installation #id", while it loads. */
export function installSubtitle(install: GitHubInstall): string {
  const login = install.details?.account.login
  if (!login) return install.installationId ? `Installation #${install.installationId}` : ''
  const type = install.details?.account.type
  return `${type ? `${type} · ` : ''}Installation #${install.installationId}`
}

/**
 * The install's repositories: a loading skeleton, the broken state, or the
 * list with its "Manage on GitHub" footer.
 */
export function InstallRepositories({
  install,
  onRemove,
}: {
  install: GitHubInstall
  /** Remove the broken install from the workspace. */
  onRemove: () => void
}) {
  const { status, details, installationId } = install
  if (status === 'loading') {
    return (
      <div className="space-y-2 py-1">
        <div className="h-3 rounded bg-muted animate-pulse w-24 mb-3" />
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-muted animate-pulse flex-shrink-0" />
            <div className="h-3 rounded bg-muted animate-pulse flex-1" />
          </div>
        ))}
      </div>
    )
  }

  if (status === 'gone') {
    return <BrokenState accountLogin={details?.account.login ?? null} onReconnect={install.reauthorize} onRemove={onRemove} />
  }

  if (status === 'unknown') {
    return (
      <div role="status" className="rounded p-3 border border-border text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-3">
        <span>Couldn’t reach GitHub to list the repositories. This doesn’t mean anything changed.</span>
        <Button size="sm" variant="outline" onClick={install.refresh}>Try again</Button>
      </div>
    )
  }

  if (!details) return null

  const repos = details.repos
  const repoCount = repos.length

  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground py-2">
        {repoCount === 0 ? 'No repositories' : `${repoCount} ${repoCount === 1 ? 'Repository' : 'Repositories'}`}
      </div>

      {repos.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">
          No repositories are accessible via this installation. Grant access to repositories on GitHub.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {repos.map((repo) => {
            const repoIdentifier = `${repo.owner}/${repo.name}`
            return (
              <li key={repoIdentifier} className="rounded border border-border bg-background overflow-hidden">
                <div className="grid items-center gap-3 py-2 px-3" style={{ gridTemplateColumns: '28px 1fr' }}>
                  <OwnerAvatar login={repo.owner} avatarUrl={repo.ownerAvatarUrl} size={28} />
                  <span className="font-mono text-sm truncate">
                    <span className="text-muted-foreground">{repo.owner} /</span>{' '}
                    {repo.name}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <div className="flex items-center justify-between mt-2 pt-2.5 border-t border-dashed border-border">
        <span className="text-xs text-muted-foreground">
          Access granted to {repoCount} {repoCount === 1 ? 'repository' : 'repositories'}
        </span>
        {installationId && (
          <a
            href={buildManageOnGitHubUrl(installationId, details.account)}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            Manage on GitHub <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  )
}

/** The App was uninstalled on GitHub: reconnect, or remove it here. */
function BrokenState({
  accountLogin,
  onReconnect,
  onRemove,
}: {
  accountLogin: string | null
  onReconnect: () => void
  onRemove: () => void
}) {
  return (
    <div className="mt-1 mb-1 rounded p-3 bg-destructive/10 border border-destructive/40 text-destructive text-xs flex flex-wrap items-center justify-between gap-3">
      <span>
        <strong>GitHub App is no longer installed</strong>
        {accountLogin && (
          <>
            {' '}on{' '}
            <code className="font-mono">{accountLogin}</code>
          </>
        )}
        . Reconnect to resume checks and issues, or remove it.
      </span>
      <span className="flex gap-2 flex-shrink-0">
        <Button size="sm" onClick={onReconnect}>
          Reconnect
        </Button>
        <Button size="sm" variant="destructive" onClick={onRemove}>
          Remove
        </Button>
      </span>
    </div>
  )
}
